import React from 'react';
import { Button } from '@chakra-ui/react';
import { WizardState } from '../types';

interface CompleteStepProps {
  state: WizardState;
  merchantId: string | null;
  onViewMerchant: () => void;
  onGoToList: () => void;
  onCreateAnother: () => void;
}

const CompleteStep: React.FC<CompleteStepProps> = ({
  state,
  merchantId,
  onViewMerchant,
  onGoToList,
  onCreateAnother,
}) => {
  return (
    <div className="space-y-8">
      {/* Success Icon and Message */}
      <div className="text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
          <svg
            className="h-10 w-10 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="mt-6 text-2xl font-bold text-gray-900">Setup Complete!</h3>
        <p className="mt-2 text-sm text-gray-500">
          Your merchant has been created successfully.
        </p>
      </div>

      {/* Summary */}
      <div className="mx-auto max-w-md rounded-xl border border-green-200 bg-green-50 p-6">
        <h4 className="text-sm font-semibold text-green-900 mb-4">Created Entities</h4>
        <ul className="space-y-2 text-sm text-green-800">
          <li className="flex items-center gap-2">
            <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Merchant: <span className="font-medium">{state.merchant.name}</span></span>
          </li>
          {state.merchant.type === 'regular' && state.subaccounts.length > 0 && (
            <li className="flex items-center gap-2">
              <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{state.subaccounts.length} Subaccount(s)</span>
            </li>
          )}
          {state.merchant.type === 'regular' && (
            (() => {
              const newFeeGroups = state.subaccounts.filter(s => s.feeGroup.mode === 'new').length;
              if (newFeeGroups > 0) {
                return (
                  <li className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{newFeeGroups} new Fee Group(s)</span>
                  </li>
                );
              }
              return null;
            })()
          )}
          {state.merchant.type === 'agent' && state.agents.length > 0 && (
            <li className="flex items-center gap-2">
              <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{state.agents.length} Agent(s)</span>
            </li>
          )}
          {state.merchant.type === 'agent' && (
            (() => {
              const totalAssignments = state.agents.reduce((acc, a) => acc + a.feeGroupAssignments.length, 0);
              const newFeeGroups = state.agents.reduce((acc, a) => acc + a.feeGroupAssignments.filter(fg => fg.feeGroup.mode === 'new').length, 0);
              return (
                <>
                  {newFeeGroups > 0 && (
                    <li className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{newFeeGroups} new Fee Group(s)</span>
                    </li>
                  )}
                  {totalAssignments > 0 && (
                    <li className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{totalAssignments} Fee Group Assignment(s)</span>
                    </li>
                  )}
                </>
              );
            })()
          )}
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
        {merchantId && (
          <Button
            onClick={onViewMerchant}
            className="btn btn-primary"
          >
            View Merchant Details
          </Button>
        )}
        <Button
          onClick={onGoToList}
          className="btn btn-default"
        >
          Go to Merchants List
        </Button>
        <Button
          onClick={onCreateAnother}
          className="btn btn-default"
        >
          Create Another Merchant
        </Button>
      </div>
    </div>
  );
};

export default CompleteStep;
